import { useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import foodLogoMark from '../../modules/food_version_final/frontend/src/iconosFoodPlan/food-logo-mark.png';
import { buildWaMeUrl } from '../utils/whatsappSupport';
import '../food-plan/food-tailwind.css';

const FOOD_LOGIN_URL = 'https://foodplan.ecosistemafit.app/login';
const FOOD_WA_URL = buildWaMeUrl(
  '573192635819',
  'Hola, vi el demo de Food Plan y quiero más información.',
);

const SWAPS = [
  { from: 'Arroz', to: 'Papa', fromIcon: 'rice', toIcon: 'potato', plate: 'Salmón y verduras' },
  { from: 'Pollo', to: 'Carne magra', fromIcon: 'chicken', toIcon: 'meat', plate: 'Pechuga a la plancha' },
  { from: 'Avena', to: 'Arepa', fromIcon: 'oats', toIcon: 'arepa', plate: 'Bowl de quinoa' },
];

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const COPY = {
  final: {
    badge: 'Nutrición sin dietas estrictas',
    titleLead: 'Come lo que te gusta.',
    titleAccent: 'Sin culpa.',
    subtitle: 'Aprende a intercambiar tus alimentos favoritos, mantén tus hábitos en verde y alcanza tus objetivos sin renunciar al sabor.',
    gaugeTitle: 'Tu adherencia semanal',
    gaugeNote: '¡90% de adherencia esta semana!',
  },
  entrenador: {
    badge: 'Máxima adherencia para tus asesorados',
    titleLead: 'Tus alumnos sí cumplen el plan.',
    titleAccent: 'Y se quedan.',
    subtitle: 'Combina la flexibilidad de los equivalentes con el Semáforo de Adherencia en tiempo real. Cero abandono y seguimiento sin colapsar tu WhatsApp.',
    gaugeTitle: 'Adherencia de tus asesorados',
    gaugeNote: '¡90% de cumplimiento esta semana!',
  },
};

const USER_BENEFITS = [
  {
    icon: '⇄',
    title: 'Libertad sin dietas estrictas',
    body: 'Con el Sistema de Equivalentes, si no hay pollo o arroz, se cambia por lo que haya en casa y los macros se mantienen en 1 clic.',
  },
  {
    icon: '🚦',
    title: 'Medición visual sin obsesión',
    body: 'Cero cálculos imposibles. El semáforo muestra al instante si el día va en 🟢 cumplimiento alto, 🟡 desviación leve o 🔴 bajo cumplimiento.',
  },
  {
    icon: '🌿',
    title: 'Construcción de hábitos reales',
    body: 'No es un programa temporal. Enseña a comer por proporciones, controlar porciones y sostener el resultado para toda la vida.',
  },
  {
    icon: '◎',
    title: 'Seguimiento integral 360°',
    body: 'En un solo lugar queda la comida, el agua, los pasos diarios y la evolución en fotos y medidas corporales.',
  },
  {
    icon: '✦',
    title: 'Cero culpa por antojos',
    body: 'El método integra alimentos flexibles o una salida de fin de semana dentro del balance, sin arruinar el progreso semanal.',
  },
];

const COACH_BENEFITS = [
  {
    icon: '📈',
    title: 'Más retención de clientes',
    body: 'Los alumnos abandonan cuando la dieta es rígida. Con el semáforo y los equivalentes la adherencia sube y se quedan más meses.',
  },
  {
    icon: '💬',
    title: 'Ahorro de horas en WhatsApp',
    body: 'En lugar de responder «¿con qué reemplazo la carne?» todo el día, la app le da autonomía al alumno.',
  },
  {
    icon: '★',
    title: 'Marca premium',
    body: 'El coach entrega nutrición con tecnología propia y puede justificar tarifas más altas en sus planes híbridos.',
  },
  {
    icon: '👀',
    title: 'Monitoreo en tiempo real',
    body: 'De un vistazo se ve quién está en verde y quién en rojo, para intervenir antes de que abandone.',
  },
  {
    icon: '🛡️',
    title: 'Protección y practicidad',
    body: 'Quita la presión de prescribir dietas restrictivas y enfoca la nutrición como hábitos y cumplimiento.',
  },
];

function BowlArt() {
  return (
    <svg className="demo-bowl" viewBox="0 0 520 420" aria-hidden="true">
      <ellipse cx="270" cy="250" rx="168" ry="118" fill="#f4efe6" />
      <ellipse cx="270" cy="236" rx="150" ry="102" fill="#fffdf8" />
      <ellipse cx="250" cy="228" rx="118" ry="78" fill="#f3e2c4" />
      <ellipse cx="214" cy="214" rx="28" ry="20" fill="#e7b15a" transform="rotate(-18 214 214)" />
      <ellipse cx="248" cy="232" rx="26" ry="18" fill="#f0c36a" transform="rotate(12 248 232)" />
      <ellipse cx="286" cy="220" rx="24" ry="16" fill="#d9a441" transform="rotate(-8 286 220)" />
      <path d="M188 168c28-8 62 6 78 28 10 14 8 28-6 36-22 12-58 4-78-18-16-18-14-38 6-46z" fill="#e07a45" />
      <path d="M210 176c18 6 34 22 36 38" fill="none" stroke="#c45c2e" strokeWidth="3" strokeLinecap="round" />
      <circle cx="318" cy="196" r="22" fill="#3f8f22" />
      <circle cx="340" cy="214" r="16" fill="#64ba30" />
      <circle cx="300" cy="214" r="14" fill="#2f7d32" />
      <circle cx="176" cy="196" r="12" fill="#e23d3d" />
      <circle cx="198" cy="188" r="9" fill="#ef5350" />
      <ellipse cx="270" cy="318" rx="150" ry="22" fill="rgba(17,24,39,0.06)" />
    </svg>
  );
}

export default function DemoLanding({ name, setName, onStart, onHiddenTrainer }) {
  const [swap, setSwap] = useState(0);
  const [audience, setAudience] = useState('final');

  useEffect(() => {
    const id = window.setInterval(() => setSwap((n) => (n + 1) % SWAPS.length), 2800);
    return () => window.clearInterval(id);
  }, []);

  const pair = SWAPS[swap];
  const copy = COPY[audience];

  const go = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="food-plan-root demo-landing">
      <header className="demo-topnav">
        <a className="demo-brand" href="#demo-hero" onClick={(e) => { e.preventDefault(); go('demo-hero'); }}>
          <img src={foodLogoMark} alt="" width={36} height={36} />
          <span>FOOD PLAN</span>
        </a>
        <nav className="demo-topnav__links" aria-label="Secciones">
          <button type="button" onClick={() => go('como-funciona')}>Cómo funciona</button>
          <button type="button" onClick={() => go('beneficios')}>Beneficios</button>
          <button type="button" onClick={() => { setAudience('entrenador'); go('demo-hero'); }}>Para entrenadores</button>
        </nav>
        <div className="demo-topnav__actions">
          <a className="demo-login" href={FOOD_LOGIN_URL}>Iniciar sesión</a>
          <button type="button" className="demo-nav-cta" onClick={() => go('demo-start')}>Probar demo</button>
        </div>
      </header>

      <section className="demo-hero" id="demo-hero">
        <div className="demo-hero__copy" id="demo-start">
          <p className="demo-kicker">🌱 {copy.badge}</p>
          <h1>
            {copy.titleLead}
            <span>{copy.titleAccent}</span>
          </h1>
          <p className="demo-subtitle">{copy.subtitle}</p>

          <div className="demo-audience" role="tablist" aria-label="Tipo de visitante">
            <button type="button" role="tab" aria-selected={audience === 'final'} onClick={() => setAudience('final')}>
              Usuario final
            </button>
            <button type="button" role="tab" aria-selected={audience === 'entrenador'} onClick={() => setAudience('entrenador')}>
              <span aria-hidden="true">📊 </span>Entrenador
            </button>
          </div>

          <label className="demo-name">
            ¿Cómo te llamas?
            <span className="demo-name__field">
              <span aria-hidden="true">👤</span>
              <input
                value={name}
                placeholder="Tu nombre"
                maxLength={40}
                onChange={(e) => setName(e.target.value)}
              />
            </span>
          </label>

          <button type="button" className="demo-cta" onClick={() => onStart(audience)}>
            Probar demo interactiva →
          </button>
          <p className="demo-micro">✓ Gratis · Sin tarjeta de crédito</p>
        </div>

        <div className="demo-stage">
          <article className="demo-gauge">
            <header>
              <strong>{copy.gaugeTitle}</strong>
              <span aria-hidden="true">ⓘ</span>
            </header>
            <div className="demo-gauge__row">
              <div className="demo-ring" aria-hidden="true">
                <svg viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="30" />
                  <circle cx="40" cy="40" r="30" className="demo-ring__value" />
                </svg>
                <b>90%</b>
              </div>
              <p>{copy.gaugeNote}</p>
            </div>
            <ul>
              {DAYS.map((day) => (
                <li key={day}><i /><span>{day}</span></li>
              ))}
            </ul>
          </article>
          <BowlArt />
          <article className="demo-exchange" key={pair.from}>
            <header>
              <strong>{pair.plate}</strong>
              <span aria-hidden="true">•••</span>
            </header>
            <div className="demo-exchange__row">
              <div>
                <span className={`demo-food-icon demo-food-icon--${pair.fromIcon}`} aria-hidden="true" />
                <small>{pair.from}</small>
              </div>
              <span className="demo-exchange__arrows" aria-hidden="true">⇄</span>
              <div>
                <span className={`demo-food-icon demo-food-icon--${pair.toIcon}`} aria-hidden="true" />
                <small>{pair.to}</small>
              </div>
              <div className="demo-exchange__ok">
                <span aria-hidden="true">🌿</span>
                <small>Macros equilibrados</small>
              </div>
            </div>
            <p>Intercambia alimentos en 1 clic manteniendo tus macros y tu semáforo en verde.</p>
          </article>
        </div>
      </section>

      <section className="demo-how" id="como-funciona">
        <p className="demo-kicker">Cómo funciona</p>
        <h2>¿Quieres ver cómo funciona? Prueba nuestro demo.</h2>
        <p>Entra como usuario o como entrenador y recorre el plan, el semáforo y los intercambios.</p>
        <button type="button" className="demo-how__cta" onClick={() => go('demo-start')}>Probar nuestro demo →</button>
      </section>

      <section className="demo-benefits" id="beneficios">
        <header className="demo-benefits__head">
          <p className="demo-kicker">Beneficios</p>
          <h2>Lo que gana cada quien</h2>
        </header>
        <div className="demo-benefits__grid">
          <div className="demo-benefits__col">
            <h3>Para el usuario final</h3>
            <p className="demo-benefits__who">Atleta / asesorado</p>
            {USER_BENEFITS.map((item) => (
              <article key={item.title} className="demo-benefit">
                <span className="demo-pillar__icon" aria-hidden="true">{item.icon}</span>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="demo-benefits__col demo-benefits__col--coach">
            <h3>Para el entrenador</h3>
            <p className="demo-benefits__who">Coach</p>
            {COACH_BENEFITS.map((item) => (
              <article key={item.title} className="demo-benefit">
                <span className="demo-pillar__icon" aria-hidden="true">{item.icon}</span>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
        <blockquote className="demo-quote">
          <p>Para el usuario es la libertad de comer lo que le gusta sin culpa; para el entrenador es la garantía de que sus alumnos sí van a cumplir el plan.</p>
        </blockquote>
      </section>

      <a className="demo-wa" href={FOOD_WA_URL} target="_blank" rel="noopener noreferrer">
        <span className="demo-wa__label">Escríbenos si quieres más información</span>
        <span className="demo-wa__btn" aria-hidden="true"><MessageCircle size={26} /></span>
      </a>
      <button type="button" className="demo-trainer-gate" onClick={onHiddenTrainer}>
        Demo trainer
      </button>
    </div>
  );
}
